// (Copyright) Confluent, Inc.

import * as ReachDialog from '@reach/dialog';
import React, { type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import VisuallyHidden from '@reach/visually-hidden';
import i18n from 'i18next';

import { VStack } from '../Stack';
import * as Typefaces from '../Typefaces';
import Icon from '../Icon';
import InlineLoader from '../InlineLoader';
import { useAnalyticsContext } from '../../../hooks/useAnalyticsContext';

type Styled = {
  props:
    | {
        allowOverflow: string;
        loading: string | null | undefined;
        maxModalWidth: string;
        showSidebar: boolean;
        textColor: string;
      }
    | null
    | undefined;
};

/* eslint-disable rulesdir/no-css-var-colors */
const CloseModal = styled.button<any>`
  display: flex;
  background: transparent;
  border: none;
  flex-direction: row-reverse;
  margin-left: auto;
  margin-right: 22px;
  margin-bottom: 14px;
  margin-top: ${({ noHeader }) => (noHeader ? '4px' : '18px')};
  & > svg {
    cursor: pointer;
    & > path {
      fill: var(--DIM_DARKEST);
    }
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

const ModalHeaderSection = styled.div<any>`
  margin: 0px 24px;
  ${({ canClose }) => (canClose === true ? '' : 'margin-top: 25px')};
`;

ModalHeaderSection.displayName = 'ModalHeader';

export const ModalBody = styled.div<any>`
  padding: ${({ noHeader }) => (noHeader ? '0px 24px 8px' : '24px')};

  > p {
    margin-top: 0;
    margin-bottom: 20px;
  }
  > *:last-child {
    margin-bottom: 0;
  }
`;
ModalBody.displayName = 'ModalBody';

export const ModalFooter = styled.div<any>`
  padding: ${({ noTopPadding }) => (noTopPadding ? '0px' : '16px')} 24px 32px;
  > button,
  > a > button {
    margin-right: 10px;
  }
`;
ModalFooter.displayName = 'ModalFooter';

/* eslint-disable rulesdir/no-css-var-colors */
export const Outer = styled(ReachDialog.DialogOverlay).attrs(() => ({
  'data-test': 'ModalOuter',
  'data-testid': 'modal-outer',
}))<any>`
  z-index: var(--MODAL_OUTER_ZINDEX);
  background-color: var(--OVERLAY_DARK);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  ${({ props }: Styled) => (props?.showSidebar ? 'left: var(--SIDEBAR_WIDTH);' : '')};
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const ModalInner = styled(ReachDialog.DialogContent).attrs(() => ({
  'data-test': 'Modal',
  'data-testid': 'modal',
}))<any>`
  padding: 0;
  margin: 0;
  background: initial;
  position: relative;
  box-shadow: var(--SHADOW_HOVER);
  max-width: ${({ props }: Styled) => props?.maxModalWidth};
  border-radius: 4px;
  background-color: ${({ background }) => (background ? background : 'var(--WHITE)')};
  color: ${({ props }: Styled) => (props?.textColor ? props?.textColor : '')};
  z-index: 1;
  flex: 1;
  flex-direction: column;
  ${({ props }: Styled) =>
    props?.allowOverflow
      ? ''
      : `max-height: min(calc(100vh - 200px), 768px);
  overflow-y: auto;`}
  ${({ props }: Styled) =>
    props?.loading != null
      ? css`
          position: fixed;
          left: -100%;
          opacity: 0;
          pointer-events: none !important;
        `
      : ''};
`;
/* eslint-enable rulesdir/no-css-var-colors */

ModalInner.displayName = 'ModalInner';

/* eslint-disable rulesdir/no-css-var-colors */
const Loader = styled(InlineLoader)`
  margin: auto;
  > div {
    background-color: var(--WHITE);
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
const Divider = styled.div`
  margin-top: 12px;
  border-bottom: 1px solid var(--DIM_LIGHTER_EVEN);
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const ModalHeader = ({
  onClose,
  subheader,
  header,
  noHeader,
}: {
  header?: string | null | undefined;
  noHeader?: boolean;
  onClose?: () => void;
  subheader?: ReactNode;
}) => (
  <div>
    {onClose != null && (
      <CloseModal data-testid="modal-close" noHeader={noHeader} onClick={onClose}>
        <Icon ariaHidden={true} name="x" size={20} />
        <VisuallyHidden>{i18n.t('core:Modal.Close')}</VisuallyHidden>
      </CloseModal>
    )}
    {header && (
      <ModalHeaderSection canClose={onClose != null}>
        <Typefaces.H2>{header}</Typefaces.H2>
        <Divider />
        {typeof subheader === 'string' ? (
          <VStack padding={{ top: 24, bottom: 12 }}>
            <Typefaces.Body>{subheader}</Typefaces.Body>
          </VStack>
        ) : (
          subheader
        )}
      </ModalHeaderSection>
    )}
  </div>
);

type ModalProps = {
  ['aria-label']?: string;
  allowOverflow?: boolean;
  background?: string;
  children?: ReactNode;
  header?: string | null | undefined;
  loading: boolean;
  maxModalWidth: 'narrow' | 'medium' | 'wide' | string;
  modalId?: string;
  noCloseButton?: boolean;
  noHeader?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  showSidebar: boolean;
  skipTracking?: boolean;
  subheader?: ReactNode;
  textColor?: string;
};

const Modal = (props: ModalProps) => {
  const {
    loading,
    showSidebar,
    maxModalWidth,
    modalId,
    noHeader,
    onClose,
    onOpen,
    subheader,
    header,
    children,
    allowOverflow,
    background,
    textColor,
    skipTracking,
    noCloseButton,
  } = props;

  const { track } = useAnalyticsContext();

  React.useEffect(() => {
    if (onOpen) {
      // Callback that is triggered when the modal is opened
      onOpen();
    }

    if (!skipTracking) {
      track((inherited) => ({
        name: 'Modal Opened',
        properties: {
          ...inherited,
          modalId,
          header,
          source: inherited.source ?? [],
        },
      }));
      return () => {
        track((inherited) => ({
          name: 'Modal Closed',
          properties: {
            ...inherited,
            modalId,
            header,
            source: inherited.source ?? [],
          },
        }));
      };
    }
  }, [header, modalId, onOpen, skipTracking, track]);

  const widthDefaults = {
    narrow: '358px',
    medium: '448px',
    wide: '568px',
  };

  const modalWidth = widthDefaults[maxModalWidth] || maxModalWidth;

  return (
    <Outer isOpen={true} onDismiss={props.onClose} props={{ showSidebar }}>
      {loading && <Loader />}
      <ModalInner
        aria-label={props['aria-label'] ?? header ?? ''}
        background={background}
        props={{
          loading: loading ? '1' : undefined,
          textColor,
          allowOverflow,
          maxModalWidth: modalWidth,
        }}
      >
        <ModalHeader
          header={header}
          noHeader={noHeader}
          onClose={noCloseButton ? undefined : onClose}
          subheader={subheader}
        />
        {children}
      </ModalInner>
    </Outer>
  );
};

Modal.defaultProps = {
  loading: false,
  maxModalWidth: 'medium',
  showSidebar: false,
};

export default Modal;
